import { createReference, ProfileResource, QueryTypes } from "@medplum/core";
import { Communication, Patient, Practitioner, Provenance, Reference } from "@medplum/fhirtypes";

import { isThisYear, isToday } from "date-fns";

import { DataOperation, ProvenanceParticipantType } from "@/fhir/enums/fhir-valuesets";
import { Systems } from "@/fhir/enums/systems";
import { partialUSDateFormat, shortUSDateFormat, timeDateFormat } from "@/utils/datetime";

import { COMMUNICATION_THREAD_CATEGORY } from "../constants/messages";

export function getMessageTime(time: string): string {
  if (isToday(time)) {
    return timeDateFormat(time);
  }

  if (isThisYear(time)) {
    return partialUSDateFormat(time);
  }

  return shortUSDateFormat(time);
}

export function getCommunicationThreadQuery(
  communication: Communication,
  profile: ProfileResource,
  extra?: Record<string, string | number | boolean | undefined>,
): QueryTypes {
  const communicationReference = createReference(communication).reference;
  const profileReference = createReference(profile).reference;

  const query: QueryTypes = {
    "part-of": communicationReference,
    status: "in-progress",
    category: COMMUNICATION_THREAD_CATEGORY,
    "sender:not": profileReference,
    recipient: profileReference,
    _revinclude: "Provenance:target",
    ...extra,
  };

  return query;
}

export function getCommunicationReadProvenance(
  communication: Communication,
  profileReference: Reference<ProfileResource>,
  attributes?: Partial<Provenance>,
): Provenance {
  const { occurredDateTime, ...rest } = attributes ?? {};

  const target: Reference<Communication>[] = [createReference(communication)];
  if (communication.partOf?.length) {
    const { partOf } = communication;
    target.push(partOf[0]! as Reference<Communication>);
  }

  return {
    ...rest,
    resourceType: "Provenance",
    target,
    recorded: new Date().toISOString(),
    occurredDateTime: occurredDateTime ?? new Date().toISOString(),
    agent: [
      {
        who: profileReference as Reference<Practitioner | Patient>,
        type: {
          coding: [
            {
              system: Systems.HL7ProvenanceParticipantType,
              code: ProvenanceParticipantType.Performer,
              display: "Performer",
            },
          ],
        },
      },
    ],
    activity: {
      coding: [
        {
          system: Systems.HL7DataOperation,
          code: DataOperation.Read,
          display: "Read",
        },
      ],
    },
  };
}
